import React, {
  PropsWithChildren,
  useContext,
  Dispatch,
  useReducer,
  ReactNode,
  useEffect,
} from "react";
import { TogglePosition } from "../components/Shared/SplitContainer/PaneCollapseToggle";

export type PaneProps = {
  content: ReactNode;
  initialSize: number;
  minSize: number;
  maxSize: number;
  isCollapsible?: boolean;
  showToggle?: boolean;
  togglePosition?: TogglePosition;
};

export type PaneState = PaneProps & { id: string; isOpen: boolean };

export type PanesState = {
  openTabId: string;
  panes: PaneState[];
};

interface ISplitContainerContextState {
  panesState: PanesState;
}

export enum SplitContainerContextActionType {
  SET_PANES_STATE = "SET_PANES_STATE",
}

type SplitContainerContextActions = {
  togglePane: (id: string) => void;
};

type SplitContainerContextReducerAction = {
  type: SplitContainerContextActionType.SET_PANES_STATE;
  payload: PanesState;
};

interface ISplitContainerContext {
  state: ISplitContainerContextState;
  dispatch: Dispatch<SplitContainerContextReducerAction>;
  actions: SplitContainerContextActions;
}

const initState: ISplitContainerContextState = {
  panesState: {
    openTabId: "",
    panes: [],
  },
};

const SplitContainerContext = React.createContext<ISplitContainerContext>({
  state: initState,
  dispatch: () => {},
  actions: { togglePane: () => {} },
});

const splitContainerContextReducer = (
  state: ISplitContainerContextState,
  action: SplitContainerContextReducerAction
): ISplitContainerContextState => {
  switch (action.type) {
    case SplitContainerContextActionType.SET_PANES_STATE:
      return { ...state, panesState: action.payload };
    default:
      return state;
  }
};

type SplitContainerContextProviderProps = {
  panes: PaneProps[];
};

export const SplitContainerContextProvider = ({
  children,
  panes,
}: PropsWithChildren<SplitContainerContextProviderProps>) => {
  const [state, dispatch] = useReducer(splitContainerContextReducer, initState);
  useEffect(() => {
    let openTabId = state.panesState.openTabId;
    const panesState: PaneState[] = [];
    for (let i = 0; i < panes.length; i++) {
      const newPane = panes[i];
      const paneId = i.toString();

      const newState = {
        ...newPane,
        id: paneId,
        isOpen: openTabId === paneId,
      };
      panesState.push(newState);
    }

    dispatch({
      type: SplitContainerContextActionType.SET_PANES_STATE,
      payload: {
        openTabId: openTabId,
        panes: panesState,
      },
    }); // eslint-disable-next-line
  }, [dispatch, panes]);

  const actions: SplitContainerContextActions = {
    togglePane: (id: string) => {
      let panes = [...state.panesState.panes];
      const index = panes.findIndex((p) => p.id === id);
      if (index === -1) return;
      const pane = panes[index];
      if (!pane.isCollapsible) return;
      pane.isOpen = !pane.isOpen;

      dispatch({
        type: SplitContainerContextActionType.SET_PANES_STATE,
        payload: {
          openTabId: pane.isOpen ? id : "",
          panes,
        },
      });
    },
  };

  return (
    <SplitContainerContext.Provider value={{ state, dispatch, actions }}>
      {children}
    </SplitContainerContext.Provider>
  );
};

export const useSplitContainerContext = () => useContext(SplitContainerContext);
